import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Bot, Send, Plus, Trash2, Download, Copy, ShieldAlert, Bug, Search,
  Radar, Zap, MessageSquare, User as UserIcon,
} from "lucide-react";

const SUGGESTED = [
  { icon: Bug, label: "Explain CVE-2024-3094 (XZ Utils backdoor) and detection queries" },
  { icon: Radar, label: "Threat-hunt query for Cobalt Strike beacons in Splunk and KQL" },
  { icon: ShieldAlert, label: "Investigate suspicious outbound to 45.155.204.7:4444" },
  { icon: Search, label: "Latest LockBit 3.0 IOCs and MITRE techniques" },
  { icon: Zap, label: "Sigma rule for detecting Kerberoasting" },
  { icon: MessageSquare, label: "Incident-response steps for a confirmed ransomware infection" },
];

function renderMarkdown(text) {
  // minimal safe render: escape → code fences → inline code → bold → line breaks
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const parts = [];
  const re = /```(\w+)?\n([\s\S]*?)```/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    parts.push({ type: "code", lang: m[1] || "text", value: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
  return parts.map((p, i) => {
    if (p.type === "code") {
      return (
        <div key={i} className="my-2 border border-white/10 rounded-sm bg-[#0a0a0a]">
          <div className="px-3 py-1 border-b border-white/5 font-mono text-[9px] uppercase tracking-widest text-[#8E9196] flex items-center justify-between">
            <span>{p.lang}</span>
            <button onClick={() => { navigator.clipboard.writeText(p.value); toast.success("Copied"); }}
              className="text-[#8E9196] hover:text-[#00F0FF]"><Copy className="w-3 h-3" /></button>
          </div>
          <pre className="p-3 font-mono text-[12px] leading-relaxed text-white/90 overflow-x-auto whitespace-pre">{p.value}</pre>
        </div>
      );
    }
    const html = esc(p.value)
      .replace(/`([^`]+)`/g, '<code class="font-mono text-[12px] bg-white/[0.06] px-1 py-0.5 rounded-sm text-[#00F0FF]">$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, "<br/>");
    // Defense-in-depth: even though `esc()` neutralizes raw HTML in the source
    // before any regex substitutions, we run the assembled markup through
    // DOMPurify to guarantee any future template additions can't reintroduce
    // an XSS surface via LLM-authored content.
    const safe = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["code", "strong", "br"],
      ALLOWED_ATTR: ["class"],
    });
    return <div key={i} dangerouslySetInnerHTML={{ __html: safe }} />;
  });
}

export default function Copilot() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);          // session object with messages
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/copilot/sessions");
      setSessions(data);
      return data;
    } catch (_e) { toast.error("Failed to load sessions"); return []; }
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/copilot/sessions/${sid}`);
      setActive(data);
    } catch (_e) { toast.error("Session unavailable"); }
  };

  const newSession = async (initialPrompt) => {
    try {
      const { data } = await api.post("/copilot/sessions");
      const list = await loadSessions();
      const created = list.find((s) => s.id === data.id) || data;
      setActive({ ...created, messages: [] });
      if (initialPrompt) {
        setTimeout(() => sendPrompt(data.id, initialPrompt), 50);
      }
      return data;
    } catch (_e) { toast.error("Could not create session"); }
  };

  const sendPrompt = async (sid, content) => {
    setSending(true);
    // optimistic user bubble
    setActive((prev) => prev && prev.id === sid ? {
      ...prev, messages: [...(prev.messages || []), { role: "user", content, timestamp: new Date().toISOString() }],
    } : prev);
    try {
      const { data } = await api.post(`/copilot/sessions/${sid}/message`, { content });
      // replace optimistic with actual + assistant
      setActive((prev) => prev && prev.id === sid ? {
        ...prev,
        messages: [...(prev.messages || []).slice(0, -1), data.user, data.assistant],
      } : prev);
      loadSessions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI unavailable");
      // rollback optimistic
      setActive((prev) => prev && prev.id === sid ? {
        ...prev, messages: (prev.messages || []).slice(0, -1),
      } : prev);
    } finally {
      setSending(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const content = input.trim();
    if (!content || sending) return;
    setInput("");
    if (!active) {
      const s = await newSession();
      if (s) await sendPrompt(s.id, content);
    } else {
      await sendPrompt(active.id, content);
    }
  };

  const deleteSession = async (s) => {
    if (!confirm(`Delete '${s.title}'?`)) return;
    try {
      await api.delete(`/copilot/sessions/${s.id}`);
      if (active?.id === s.id) setActive(null);
      loadSessions();
    } catch (_e) { toast.error("Delete failed"); }
  };

  const downloadPdf = async () => {
    if (!active) return;
    try {
      const token = localStorage.getItem("forensiq_token");
      const res = await fetch(`${API_BASE}/copilot/sessions/${active.id}/report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ForensiQ-Copilot-${active.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (_e) { toast.error("Download failed"); }
  };

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages?.length, sending]);

  const messages = active?.messages || [];

  return (
    <div className="h-screen flex bg-[#060606] text-white">
      {/* Sessions sidebar */}
      <aside className="w-72 border-r border-white/10 flex flex-col bg-[#0a0a0a] shrink-0">
        <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Sessions</div>
            <div className="font-display text-lg">Chat history</div>
          </div>
          <button onClick={() => newSession()} data-testid="new-chat-btn"
            title="New chat"
            className="p-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black rounded-sm">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {sessions.length === 0 && (
            <div className="text-xs text-[#52525B] px-4 py-6 text-center">No chats yet. Ask something below.</div>
          )}
          {sessions.map((s) => (
            <div key={s.id}
              onClick={() => openSession(s.id)}
              data-testid={`session-${s.id}`}
              className={`group px-4 py-2.5 cursor-pointer border-l-2 ${
                active?.id === s.id ? "border-[#00F0FF] bg-white/5" : "border-transparent hover:bg-white/[0.03]"
              }`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{s.title}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mt-0.5">
                    {new Date(s.updated_at).toLocaleString()}
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s); }}
                  className="p-1 text-[#8E9196] hover:text-[#FF3B30] opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="px-8 py-5 border-b border-white/10 flex items-center justify-between shrink-0 bg-[#0a0a0a]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 border border-[#00F0FF] rounded-sm flex items-center justify-center shrink-0">
              <Bot className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Threat Hunting AI · security-tuned</div>
              <div className="font-display text-xl truncate">{active?.title || "New conversation"}</div>
            </div>
          </div>
          {active && messages.length > 0 && (
            <button onClick={downloadPdf} data-testid="download-transcript-btn"
              className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
              <Download className="w-3.5 h-3.5" /> Transcript PDF
            </button>
          )}
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 md:px-12 py-6" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="max-w-3xl mx-auto py-8">
              <div className="text-center">
                <div className="inline-flex w-14 h-14 border border-[#00F0FF] rounded-sm items-center justify-center mb-4">
                  <Radar className="w-6 h-6 text-[#00F0FF]" strokeWidth={1.5} />
                </div>
                <h1 className="font-display font-black text-4xl">Ask your security AI anything.</h1>
                <p className="text-[#8E9196] mt-2">CVEs, malware families, threat hunting queries, MITRE mappings, IOC analysis, incident response — ForensiQ AI primed with a SOC system prompt.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8">
                {SUGGESTED.map((s, i) => (
                  <button key={i} onClick={() => newSession(s.label)}
                    data-testid={`suggested-${i}`}
                    className="text-left border border-white/10 hover:border-[#00F0FF] bg-[#121212] rounded-sm p-4 transition-colors group">
                    <div className="flex items-center gap-2">
                      <s.icon className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.5} />
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] group-hover:text-white">Try it →</span>
                    </div>
                    <div className="mt-2 text-sm text-white/90">{s.label}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`msg-${i}`}>
                  <div className="shrink-0 w-8 h-8 border border-white/15 rounded-sm flex items-center justify-center">
                    {m.role === "user" ? <UserIcon className="w-4 h-4 text-white" strokeWidth={1.5} /> : <Bot className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.5} />}
                  </div>
                  <div className={`min-w-0 flex-1 ${m.role === "user" ? "text-right" : ""}`}>
                    <div className={`inline-block max-w-full text-left rounded-sm p-4 ${
                      m.role === "user" ? "bg-[#00F0FF]/[0.08] border border-[#00F0FF]/30" : "bg-[#121212] border border-white/10"
                    }`}>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">
                        {m.role === "user" ? "You" : "Copilot"} · {new Date(m.timestamp).toLocaleTimeString()}
                      </div>
                      <div className="text-sm leading-relaxed break-words">{renderMarkdown(m.content || "")}</div>
                    </div>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-3">
                  <div className="shrink-0 w-8 h-8 border border-[#00F0FF] rounded-sm flex items-center justify-center">
                    <Bot className="w-4 h-4 text-[#00F0FF] animate-pulse" />
                  </div>
                  <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full animate-pulse" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full animate-pulse" style={{ animationDelay: "200ms" }} />
                      <span className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full animate-pulse" style={{ animationDelay: "400ms" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={submit} className="border-t border-white/10 bg-[#0a0a0a] px-6 md:px-12 py-4 shrink-0" data-testid="chat-form">
          <div className="max-w-3xl mx-auto flex items-end gap-2 border border-white/15 focus-within:border-[#00F0FF] rounded-sm bg-[#121212] px-3 py-2">
            <textarea rows={1} value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }}
              placeholder="Ask about a CVE, IOC, malware family, hunt query, incident response step…"
              data-testid="chat-input"
              className="flex-1 bg-transparent resize-none outline-none font-mono text-sm placeholder:text-[#52525B] max-h-40 min-h-[24px]" />
            <button disabled={sending || !input.trim()} data-testid="chat-send"
              className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-1.5 rounded-sm disabled:opacity-40">
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="max-w-3xl mx-auto mt-2 text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
            Enter to send · Shift+Enter for newline · Powered by ForensiQ AI
          </div>
        </form>
      </div>
    </div>
  );
}
